import { useState, useEffect, Suspense, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useUser } from '@/contexts/user-context';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle, Building2, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { TenantLogo } from '@/components/shared/tenant-logo';
import { getTenantFromSubdomain, getCurrentTenantId, setTenantId } from '@/lib/tenant';
import { useBranding } from '@/contexts/branding-context';

// Login schema - tenantId is always optional
const loginSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  password: z.string().min(1, { message: 'Password is required' }),
  tenantId: z.string().optional(),
  rememberMe: z.boolean().optional(),
});

type LoginFormValues = z.infer<typeof loginSchema>;

const TENANT_STORAGE_KEY = 'tenant_id'; // Aligned with lib/tenant.ts

function LoginPageContent() {
  const { t } = useTranslation();
  const { login, isLoading } = useUser();
  const { refetch: refetchBranding } = useBranding();
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  // Check if we have any tenant context (subdomain or localStorage)
  const currentTenantId = useMemo(() => {
    return getCurrentTenantId();
  }, []);

  const hasSubdomain = useMemo(() => {
    const tenant = getTenantFromSubdomain();
    return Boolean(tenant);
  }, []);

  // Only require manual tenant input if no subdomain AND no saved tenant
  const requireTenant = !currentTenantId;
  // Always show tenant field on localhost for dev
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1');
  const showTenantField = requireTenant || isLocalhost;

  useEffect(() => {
    // Check for access denied error in URL
    const errorParam = searchParams.get('error');
    if (errorParam === 'accessDenied') {
      setError(
        "Regular users don't have access to the admin system. Please use the member portal instead."
      );
    }
  }, [searchParams]);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      tenantId: '',
      rememberMe: false,
    },
  });

  // Load saved tenant from localStorage or current context on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && showTenantField) {
      const savedTenant = localStorage.getItem(TENANT_STORAGE_KEY);
      const tenantToUse = savedTenant || currentTenantId;
      if (tenantToUse) {
        form.setValue('tenantId', tenantToUse);
      }
    }
  }, [showTenantField, currentTenantId, form]);

  // Save tenant to localStorage and re-fetch branding whenever it changes
  const handleTenantChange = (value: string) => {
    form.setValue('tenantId', value);
    if (value.trim()) {
      console.log('[Login] Saving tenant to localStorage:', value.trim());
      setTenantId(value.trim());
      refetchBranding();
    }
  };

  // Clear tenant from form and localStorage
  const handleClearTenant = () => {
    console.log('[Login] Clearing tenant from localStorage');
    if (typeof window !== 'undefined') {
      localStorage.removeItem(TENANT_STORAGE_KEY);
    }
    form.setValue('tenantId', '');
    form.setFocus('tenantId');
    // Reload the page to reset tenant context
    window.location.reload();
  };

  async function onSubmit(data: LoginFormValues) {
    try {
      setError(null);

      // Validate tenant input if required
      if (requireTenant && !data.tenantId?.trim()) {
        setError('Please enter your gym ID');
        return;
      }

      const result = await login({
        email: data.email,
        password: data.password,
        tenantId: data.tenantId?.trim(),
      });

      // If login returns null, it means access was denied (handled in the login function)
      if (!result) {
        setError(
          "Regular users don't have access to the admin system. Please use the member portal instead."
        );
      }
      // Redirect is handled in the login function
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred');
      }
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-black p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-6">
            <TenantLogo className="h-14 w-auto" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">
            {t('auth.login')}
          </CardTitle>
          <CardDescription className="text-center">
            {t('auth.loginDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {/* Tenant Input - Show when required or on localhost (dev) */}
              {showTenantField && (
                <FormField
                  control={form.control}
                  name="tenantId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Gym ID
                      </FormLabel>
                      <div className="relative">
                        <FormControl>
                          <Input
                            placeholder="Enter your gym ID (e.g., flexgym)"
                            value={field.value || ''}
                            onChange={(e) => handleTenantChange(e.target.value)}
                            onBlur={field.onBlur}
                            name={field.name}
                            ref={field.ref}
                            className="pr-10"
                          />
                        </FormControl>
                        {field.value && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-0 top-0 h-10 w-10 hover:bg-transparent"
                            onClick={handleClearTenant}
                            tabIndex={-1}
                            title="Clear gym ID"
                          >
                            <X className="h-4 w-4 text-muted-foreground hover:text-destructive transition-colors" />
                          </Button>
                        )}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.email')}</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="john.doe@example.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.password')}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="********"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex items-center justify-between">
                <FormField
                  control={form.control}
                  name="rememberMe"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="text-sm font-medium leading-none cursor-pointer">
                        {t('auth.rememberMe')}
                      </FormLabel>
                    </FormItem>
                  )}
                />
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:underline"
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  t('auth.loginButton')
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
        {/* <CardFooter className="flex flex-col space-y-4">
          <div className="text-sm text-center">
            {t("auth.noAccount")}{" "}
            <Link href="/register" className="text-primary hover:underline">
              {t("auth.register")}
            </Link>
          </div>
        </CardFooter> */}
      </Card>
    </div>
  );
}

function LoginPageLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-black p-4">
      <Card className="w-full max-w-md">
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<LoginPageLoading />}>
      <LoginPageContent />
    </Suspense>
  );
}
